import { fetchOsmOverpass } from 'api/otherApi';
import { TARGET_RIVER_RADIUS_METERS, TARGET_WATER_OVERPASS_TIMEOUT_SECONDS } from 'constants/simulation';

type LatLng = {
  lat: number;
  lng: number;
};

type LakeDetectionArea = {
  center: LatLng;
  radiusMeters: number;
};

type OverpassElement = {
  type?: string;
  id?: number;
  tags?: {
    natural?: string;
    water?: string;
    waterway?: string;
  };
};

type OverpassResponse = {
  elements?: OverpassElement[];
};

const buildWaterQuery = ({ center, radiusMeters }: LakeDetectionArea): string | null => {
  const lat = Number(center?.lat);
  const lng = Number(center?.lng);
  const lakeRadius = Number(radiusMeters);

  if (![lat, lng, lakeRadius].every(Number.isFinite) || lakeRadius <= 0) {
    return null;
  }

  return `[out:json][timeout:${TARGET_WATER_OVERPASS_TIMEOUT_SECONDS}];
(
  way["natural"="water"]["water"="lake"](around:${lakeRadius},${lat},${lng});
  relation["natural"="water"]["water"="lake"](around:${lakeRadius},${lat},${lng});
);
out tags 1;
(
  way["waterway"="river"](around:${TARGET_RIVER_RADIUS_METERS},${lat},${lng});
  relation["waterway"="river"](around:${TARGET_RIVER_RADIUS_METERS},${lat},${lng});
);
out tags 1;`;
};

export const detectWaterFeaturesInArea = async (
  { center, radiusMeters }: LakeDetectionArea,
  signal?: AbortSignal
): Promise<{ hasLake: boolean; hasRiver: boolean }> => {
  const query = buildWaterQuery({ center, radiusMeters });
  if (!query) {
    return { hasLake: false, hasRiver: false };
  }

  const response = await fetchOsmOverpass(query, signal);

  if (!response.ok) {
    throw new Error(`OSM water detection failed with status ${response.status}`);
  }

  const payload = (await response.json()) as OverpassResponse;
  if (!Array.isArray(payload?.elements) || payload.elements.length === 0) {
    return { hasLake: false, hasRiver: false };
  }

  let hasLake = false;
  let hasRiver = false;

  payload.elements.forEach((element) => {
    if (element?.tags?.natural === 'water' && element?.tags?.water === 'lake') {
      hasLake = true;
    }

    if (element?.tags?.waterway === 'river') {
      hasRiver = true;
    }
  });

  return { hasLake, hasRiver };
};
